import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { clamp } from 'es-toolkit';
import { useSlider } from '../../SliderContext';
import { alignToJustifyContent } from '../constants';
import type { PaginationVariantBaseProps } from '../types';
import { Bullet } from './Bullet';

const DOT_WIDTH = 24;

export interface BulletsProps extends PaginationVariantBaseProps {
  maxPaginationElementsCount?: number;
  activePaginationElementViewIndex?: number;
}

export function Bullets({
  currentIndex,
  slides,
  goTo,
  paginationClassName,
  colors,
  autoplayProgressBar,
  autoplayEnabled,
  autoplayPauseOnHover,
  seconds,
  loop,
  align,
  maxPaginationElementsCount = 5,
  activePaginationElementViewIndex = 2,
  shouldUseNewAppearance = false
}: BulletsProps) {
  const { appClassnamePrefix } = useSlider();

  const { color, active } = colors;

  const dotsCount = Math.min(slides.length, maxPaginationElementsCount);
  const innerWidth = dotsCount * DOT_WIDTH;
  const slidesCount = slides.length - 1;

  const clampedActivePaginationElementViewIndex = clamp(
    activePaginationElementViewIndex,
    0,
    slidesCount
  );

  const maxInsetMultiplier = clamp(slides.length - dotsCount, 0, slidesCount);

  const currentInsetMultiplier = clamp(
    currentIndex - clampedActivePaginationElementViewIndex,
    0,
    maxInsetMultiplier
  );

  const currentLeftInset = -currentInsetMultiplier * DOT_WIDTH;

  return (
    <Dots
      _paginationClass={paginationClassName}
      _appClassPrefix={appClassnamePrefix}
    >
      <Inner
        _appClassPrefix={appClassnamePrefix}
        _activeColor={active}
        _align={align}
        _defaultColor={color}
        _innerWidth={innerWidth}
        _leftInset={currentLeftInset}
        _paginationClass={paginationClassName}
        _slidesLength={slides.length}
      >
        {slides.map((item, index) => {
          return (
            <Bullet
              key={`${item.key}${index}`}
              currentIndex={currentIndex}
              index={index}
              slidesLength={slides.length}
              paginationClassName={paginationClassName}
              goTo={goTo}
              seconds={seconds}
              autoplayProgressBar={autoplayProgressBar}
              autoplayEnabled={autoplayEnabled}
              autoplayPauseOnHover={autoplayPauseOnHover}
              active={currentIndex === index}
              last={slidesCount === currentIndex && !loop}
              color={color}
              withShadow={shouldUseNewAppearance}
            />
          );
        })}
      </Inner>
    </Dots>
  );
}

interface DotsProps {
  _paginationClass: string;
  _appClassPrefix: string;
}

const Dots = styled.div.attrs<DotsProps>(
  ({ _paginationClass, _appClassPrefix }) => ({
    className: classNames(_paginationClass, `${_appClassPrefix}-bullets-dots`)
  })
)`
  margin-top: 4px;
  margin-bottom: 4px;
  display: inline-flex;
`;

interface InnerProps {
  _paginationClass: string;
  _appClassPrefix: string;
  _activeColor: string;
  _align: string;
  _defaultColor: string;
  _innerWidth: number;
  _leftInset: number;
  _slidesLength: number;
}

const Inner = styled.div.attrs<InnerProps>(
  ({ _paginationClass, _appClassPrefix }) => ({
    className: classNames(
      { [`${_paginationClass}-inner`]: _paginationClass },
      `${_appClassPrefix}-bullets-inner`
    )
  })
)`
  ${({
    _activeColor,
    _align,
    _defaultColor,
    _innerWidth,
    _leftInset,
    _paginationClass,
    _slidesLength
  }) => {
    const justifyValue = alignToJustifyContent(_align);

    return css`
      display: inline-flex;
      overflow: hidden;

      width: ${_innerWidth}px;

      ${_slidesLength < 3 &&
      justifyValue &&
      `justify-content: ${justifyValue};`}

      & .${_paginationClass}-dot {
        background-color: ${_defaultColor};
      }

      &
        .${_paginationClass}-item:hover
        .${_paginationClass}-dot,
        &
        .${_paginationClass}-item-active
        .${_paginationClass}-dot {
        background-color: ${_activeColor};
      }

      & .${_paginationClass}-item {
        left: ${_leftInset}px;
      }
    `;
  }}
`;
